import React from 'react';
import { ComboBoxControl } from '../../../controls/ComboBoxControl';
import { RoundIndicator } from '../../../controls/RoundIndicator';

const ItemType = {
    PrintFilm: 'PFILM',
    Film: 'FILM',
    Adhesive: 'ADHESIVE',
    Coating: 'COATING'
}

const ItemCategory = {
    Film: 'FILM',
    Adhesive: 'ADHESIVE',
    Coating: 'COATING'
}

export class MaterialEditorComponent extends React.Component {

    constructor(props) {
        super(props);

        this.settings = props.config.settings;
        this.enforceStrictFilmLayerOrder = this.settings.enforceStrictFilmLayerOrder === "True";
        this.excludeFilmWidthFromValidation = this.settings.excludeFilmWidthFromValidation === "True";
        this.allowInvalidStructureLayerSave = this.settings.allowInvalidStructureLayerSave === "True";

        if (!this.props.dataManager) {
            throw new Error("ControlDataManager Property is required");
        }

        if (!this.props.propertyName) {
            throw new Error("PropertyName Property is required");
        }

        this.filmWidth = this.props.filmWidth ? this.props.filmWidth : null;
        this.isPrelam = this.props.isPrelam ? this.props.isPrelam : false;


        this.dataManager = this.props.dataManager;
        this.dataManager.register(this);
        this.metaData = null;

        this.printFilmSourceItems = null;
        this.filmSourceItems = null;
        this.adhesiveSourceItems = null;
        this.coatingSourceItems = null;
        this.coatingsAndAdhesiveSourceItems = null;
        this.allSourceItems = null;


        this.state = {
            loaded: false,
            selectedItem: null,
            layers: []
        };

        this.onAddClick = this.onAddClick.bind(this);
        this.onDeleteClick = this.onDeleteClick.bind(this);
    }

    componentDidMount() {
        this.dataManager.bindControl(this.props.propertyName);
    }

    componentWillUnmount() {
        this.dataManager.unbindControl(this.props.propertyName);
    }

    setValue(values) {
        var items = values && values.length > 0 ? values : [];

        this.setState({
            loaded: false
        });

        let layers = [];
        items.forEach(item => {
            let layer = {
                type: this.getItemCategory(item.dataObject2.typeCode),
                dataObject2: item.dataObject2,
                dataObject2Id: item.dataObject2.id
            }
            layers.push(layer);
        });

        // A coating, if present, is always the outermost layer (index 0).
        // This mirrors the backend Resort so the editor shows exactly what will be persisted.
        layers = this.floatCoatingToTop(layers);
        layers.forEach((layer, i) => { layer.sort = i; });

        this.forceUpdate(() => {
            this.setState({
                layers: layers,
                selectedItem: null,
                loaded: true
            }, () => {
                this.dataManager.reportChange(layers, this);
            });
        });
    }

    getItemCategory(type) {
        switch (type) {
            case ItemType.Film:
            case ItemType.PrintFilm:
                return ItemCategory.Film;
            case ItemType.Adhesive:
                return ItemCategory.Adhesive;
            case ItemType.Coating:
                return ItemCategory.Coating;
        }
        return null;
    }

    floatCoatingToTop(layers) {
        // Prelams have no coating
        if (this.isPrelam) {
            return layers;
        }

        var coating = layers.find(l => l.type === ItemCategory.Coating);
        if (coating && layers.indexOf(coating) !== 0) {
            layers = layers.filter(l => l !== coating);
            layers.unshift(coating);
        }

        return layers;
    }

    hasLeadingCoating() {
        var layers = this.state.layers;
        return layers && layers.length > 0 && layers[0].type === ItemCategory.Coating;
    }


    getValue() {
        return this.state.layers;
    }

    isInputValid() {
        var layers = this.state.layers;

        // Opted-in environments may save structurally invalid specs.
        // The red indicators still complain, but saving is allowed.
        if (this.allowInvalidStructureLayerSave) {
            return !!(layers && layers.length > 0);
        }

        if (!layers || layers.length < 2) {
            return false;
        }
        // Valid configurations must terminate on a film
        if (layers[layers.length - 1].type !== ItemCategory.Film) {
            return false;
        }
        return layers.every((layer, idx) => this.getLayerValidation(idx, layer).valid);
    }

    init(metaData) {
        var self = this;
        return new Promise(function (resolve, reject) {
            self.metaData = metaData;

            self.loadDataSourcesAsync()
                .then(results => {
                    self.filmSourceItems = results.films;
                    self.printFilmSourceItems = results.printFilms;
                    self.adhesiveSourceItems = results.adhesives;
                    self.coatingSourceItems = results.coatings;
                    self.coatingsAndAdhesiveSourceItems = results.coatingsAndAdhesives;
                    self.allSourceItems = results.all;

                    self.setState({
                        loaded: true
                    });

                    resolve(self);
                });
        });
    }


    loadDataSourcesAsync() {
        return new Promise((resolve, reject) => {
            Promise.all([
                this.dataManager.getDataSourceItems('items')
            ])
                .then((results) => {
                    let printFilms = results[0].filter(obj => {
                        return obj.typeCode === ItemType.PrintFilm
                    });

                    let films = results[0].filter(obj => {
                        return this.enforceStrictFilmLayerOrder
                            ? obj.typeCode === ItemType.Film 
                            : obj.typeCode === ItemType.Film || obj.typeCode === ItemType.PrintFilm;
                    });

                    let adhesives = results[0].filter(obj => {
                        return obj.typeCode === ItemType.Adhesive
                    });

                    let coatings = results[0].filter(obj => {
                        return obj.typeCode === ItemType.Coating
                    });

                    let coatingsAndAdhesives = results[0].filter(obj => {
                        return obj.typeCode === ItemType.Coating || obj.typeCode === ItemType.Adhesive
                    });

                    let all = results[0].filter(obj => {
                        return obj.typeCode === ItemType.Adhesive || obj.typeCode === ItemType.PrintFilm || obj.typeCode === ItemType.Film || obj.typeCode === ItemType.Coating
                    });

                    let items = {
                        printFilms: printFilms,
                        films: films,
                        adhesives: adhesives,
                        coatings: coatings,
                        coatingsAndAdhesives: coatingsAndAdhesives,
                        all: all
                    }
                    resolve(items);
                })
                .catch(ex => reject(ex));
        });
    }


    onAddClick() {
        let film = this.state.selectedItem;

        var type = this.getItemCategory(film.typeCode);

        let item = {
            type: type,
            dataObject2: film
        }

        let layers = this.state.layers;
        layers.push(item);

        this.setValue(layers);
    }

    onDeleteClick(idx) {
        let layers = this.state.layers;
        layers.splice(idx, 1);
        this.setLayers(layers);
        this.setValue(layers);
    }

    setLayers(layers) {
        this.setState({
            loaded: false
        });

        this.forceUpdate(() => {
            this.setState({
                layers: layers,
                selectedItem: null,
                loaded: true
            });
        });
    }


    isLatestLayerFilm() {
        let layers = this.state.layers;
        if (layers && layers.length > 0) {
            return layers[layers.length - 1].type === ItemCategory.Film;
        }
        return false;
    }

    getLatestLayerType() {
        let layers = this.state.layers;
        if (layers && layers.length > 0) {
            return layers[layers.length - 1].type;
        }
        return null;
    }


    getLayerName(layer) {
        switch (layer.dataObject2.typeCode) {
            case ItemType.PrintFilm:
                return 'Print Web'
            case ItemType.Film:
                return 'Film'
            case ItemType.Coating:
                return 'Coating'
            case ItemType.Adhesive:
                return 'Adhesive'
            default:
                return '';
        }
    }


    getFilms() {
        if (this.isPrelam && this.filmWidth) {
            return this.filmSourceItems.filter(obj => obj.filmWidth === this.filmWidth);
        }

        // The print web is the first structural film; until one is chosen, offer print films.
        let printWeb = this.state.layers.find(l => l.type === ItemCategory.Film);
        if (!printWeb) {
            return this.printFilmSourceItems;
        }
        return this.filmSourceItems.filter(obj => obj.filmWidth === printWeb.dataObject2.filmWidth);
    }

    // Validates a single layer against the film/adhesive rules, accounting for the optional
    // leading coating (which shifts the film/adhesive parity by one). Shared by the row color
    // indicator and the Save button gate.
    getLayerValidation(idx, layer) {
        var typeCode = layer.dataObject2.typeCode;

        // A coating is exempt from the film/adhesive rules and valid in the outermost slot.
        // Prelams are films + adhesives only.
        if (typeCode === ItemType.Coating) {
            return this.isPrelam
                ? { valid: false, message: 'A prelam cannot contain a coating' }
                : { valid: true };
        }

        // Structural index ignoring the optional leading coating.
        var s = idx - (this.hasLeadingCoating() ? 1 : 0);

        if (this.enforceStrictFilmLayerOrder && this.props.isPrelam !== true) {
            if (s === 0 && typeCode !== ItemType.PrintFilm) {
                return { valid: false, message: 'This assignment requires a Print Film' };
            }
            if (s > 0 && s % 2 === 0 && typeCode !== ItemType.Film) {
                return { valid: false, message: 'This assignment requires a Film' };
            }
        }
        else {
            if (s > 0 && s % 2 === 0 && typeCode !== ItemType.Film && typeCode !== ItemType.PrintFilm) {
                return { valid: false, message: 'This assignment requires a Film or Print Film' };
            }
        }

        if (s > 0 && s % 2 !== 0 && typeCode !== ItemType.Adhesive) {
            return { valid: false, message: 'This assignment requires an Adhesive' };
        }

        return { valid: true };
    }

    getIndicatorCompontent(idx, layer) {
        var result = this.getLayerValidation(idx, layer);
        return result.valid
            ? <RoundIndicator indicate={'success'} text={'This item assignment is valid'} />
            : <RoundIndicator indicate={'error'} text={result.message} />;
    }

    getItemInputControl() {
        // After a film, offer a coating (full structures only, at most one) or an adhesive.
        if (this.getLatestLayerType() === ItemCategory.Film) {
            var coatingAllowed = !this.isPrelam && !this.state.layers.some(l => l.dataObject2.typeCode === ItemType.Coating);
            return this.renderItemPicker('adhesiveddl', coatingAllowed ? this.coatingsAndAdhesiveSourceItems : this.adhesiveSourceItems);
        }

        // Empty stack, only a coating, or after an adhesive: add a film (the print web until one is chosen).
        return this.renderItemPicker('filmddl', this.getFilms());
    }

    renderItemPicker(id, items) {
        return <ComboBoxControl id={id} items={items} alternativeItems={this.allSourceItems} displayPropertyName={'description'} onAddClick={this.onAddClick} valuePropertyName={'id'} onSelected={item => { this.setState({ selectedItem: item }) }} />;
    }


    render() {
        return (
            <div className='materialEditor'>
                {this.state.loaded &&
                    <div>
                        <div className='table'>
                            <div className='table-header'>
                                <div className='row'>
                                    <div className='col col-1'>Valid</div>
                                    <div className='col col-2'>Structure</div>
                                    <div className='col col-2'>Type</div>
                                    <div className='col col-2'>Code</div>
                                    <div className='col col-4'>Name</div>
                                    <div className='col col-1'></div>
                                </div>
                            </div>

                            <div className='body'>
                            {this.state.layers.map((layer, idx) => {
                                return (
                                    <div className='row' key={'layer' + idx}>
                                        <div className='col col-1'>{this.getIndicatorCompontent(idx, layer)}</div>
                                        <div className='col col-2'>{this.getLayerName(layer)}</div>
                                        <div className='col col-2 color-orange'>{layer.dataObject2.typeCode}</div>
                                        <div className='col col-2 color-orange'>{layer.dataObject2.code ? layer.dataObject2.code : 'TBD'}</div>
                                        <div className='col col-4'>{layer.dataObject2.description}</div>
                                        <div className='col col-1'>
                                            <button className='btn btn-secondary-outline btn-sm pull-right' disabled={layer.dataObject2.typeCode !== ItemType.Coating && idx < this.state.layers.length - 1} onClick={() => { this.onDeleteClick(idx) }}><span className='glyphicon glyphicon-trash'></span></button>
                                        </div>
                                    </div>
                                )
                            })}
                            </div>
                        </div>

                        <div>
                            {this.getItemInputControl() }
                        </div>

                    </div>
                }
            </div>
        )
    }
}
